import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiSelect from 'features/ui/base-components/pai-select';
import PaiMenuItem from 'features/ui/base-components/pai-menu-item';

interface AgencyOption {
  id: string;
  tenantName: string;
  userName: string;
  email: string;
}

interface Props {
  open: boolean;
  loading: boolean;
  tenantName?: string;
  currentAgencyName?: string;
  selectedAgencyTenantId: string | null;
  agencyOptions: AgencyOption[];

  onChangeSelectedAgency(event: React.ChangeEvent<HTMLInputElement>): void;
  onUnbind(): void;
  onSave(): void;
  onClose(): void;
}

const AgencyBindingDialog: React.FC<Props> = ({
  open,
  loading,
  tenantName,
  currentAgencyName,
  selectedAgencyTenantId,
  agencyOptions,
  onChangeSelectedAgency,
  onUnbind,
  onSave,
  onClose,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <Dialog classes={{ paper: classes.paper }} open={open} fullWidth={true} maxWidth={'xs'}>
      <DialogTitle className={classes.title}>
        <Stack direction={'row'} alignItems={'center'} justifyContent={'center'} gap={1}>
          <div className={classes.headerIcon}>A</div>
          <Typography className={classes.titleText}>{t('agency')}</Typography>
        </Stack>
        <IconButton size="small" className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack direction={'column'} gap={2}>
          {tenantName ? (
            <Stack direction={'row'} alignItems={'center'} gap={1}>
              <div className={classes.sectionIcon}>{tenantName.charAt(0)}</div>
              <Typography className={classes.subtitle}>
                {t('client')}: {tenantName}
              </Typography>
            </Stack>
          ) : null}
          {currentAgencyName ? (
            <Typography className={classes.currentAgency}>
              {t('current')}: {currentAgencyName}
            </Typography>
          ) : (
            <Typography className={classes.currentAgencyMuted}>{t('noAgencyAssigned') || 'No agency assigned'}</Typography>
          )}

          <Typography className={classes.fieldLabel}>{t('selectAgency') || 'Select agency'}</Typography>
          <PaiSelect value={selectedAgencyTenantId || ''} onChange={onChangeSelectedAgency} dense={true}>
            {agencyOptions.map((opt) => (
              <PaiMenuItem key={opt.id} value={opt.id}>
                <Stack direction={'row'} alignItems={'center'} gap={1}>
                  <div className={classes.avatar}>{opt.tenantName?.charAt(0) || 'A'}</div>
                  <Stack>
                    <Typography className={classes.optionTitle}>{opt.tenantName}</Typography>
                    <Typography className={classes.optionSubtitle}>
                      {opt.userName} · {opt.email}
                    </Typography>
                  </Stack>
                </Stack>
              </PaiMenuItem>
            ))}
          </PaiSelect>
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Stack direction={'row'} alignItems={'center'} gap={2} justifyContent={'space-between'} width={'100%'}>
          <PaiLink text={t('cancel')} size={'md'} underline={'always'} action={onClose} />
          <Stack direction={'row'} alignItems={'center'} gap={2}>
            <PaiButton
              text={t('remove')}
              border={'rounded'}
              size={'small'}
              color={'secondary'}
              action={onUnbind}
              disabled={!currentAgencyName}
            />
            <PaiButton
              text={t('save')}
              border={'rounded'}
              size={'small'}
              action={onSave}
              loading={loading}
              disabled={!selectedAgencyTenantId}
            />
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    position: 'relative',
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  headerIcon: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    background: '#FFF',
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: theme.typography.pxToRem(12),
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  closeButton: {
    position: 'absolute',
    right: 8,
    top: 8,
    color: '#FFF',
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
  subtitle: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.text.secondary,
  },
  currentAgency: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.text.primary,
    fontWeight: 500,
  },
  currentAgencyMuted: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
  fieldLabel: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.text.secondary,
  },
  sectionIcon: {
    width: 24,
    height: 24,
    borderRadius: 6,
    background: theme.palette.action.hover,
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: theme.typography.pxToRem(12),
  },
  avatar: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    background: theme.palette.backgrounds.main_new,
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: theme.typography.pxToRem(12),
    border: `1px solid ${theme.palette.divider}`,
  },
  optionTitle: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 600,
  },
  optionSubtitle: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.text.secondary,
  },
}));

export default AgencyBindingDialog;
